import { createContext, useContext, useEffect, useMemo, useState } from "react"
import moment from "moment"

const FormatContext = createContext(null)

// Backend SQL datetime format
const SQL_DATETIME_FORMAT = "YYYY-MM-DD HH:mm:ss.SSSSSS"

export const FormatProvider = ({ children }) => {
    const [dateFormat, setDateFormat] = useState(
        () => localStorage.getItem("date_format") || "DD/MM/YYYY hh:mm:ss A",
    )

    const [decimalFormat, setDecimalFormat] = useState(() => {
        const stored = localStorage.getItem("decimal_format")
        return stored ? Number(stored) : 1
    })

    // Persist changes
    useEffect(() => {
        localStorage.setItem("date_format", dateFormat)
    }, [dateFormat])

    useEffect(() => {
        localStorage.setItem("decimal_format", decimalFormat)
    }, [decimalFormat])

    // ✅ SINGLE, SAFE DATE PARSER + FORMATTER
    const formatDate = useMemo(() => {
        return (value) => {
            if (!value) return "N/A"

            let m

            // 1️⃣ Timestamp (number)
            if (typeof value === "number") {
                m = moment(value)
            }

            // 2️⃣ SQL datetime string
            else if (typeof value === "string" && value.includes(" ") && value.includes("-")) {
                m = moment(value, SQL_DATETIME_FORMAT, true)
            }

            // 3️⃣ ISO or fallback
            else {
                m = moment(value)
            }

            return m.isValid() ? m.format(dateFormat) : "N/A"
        }
    }, [dateFormat])

    const formatNumber = useMemo(() => {
        return (value) => {
            if (value === null || value === undefined) return "N/A"
            // TODO: Need to handle it when language localization is added (for French and English)
            return Number(value).toLocaleString("en-US", {
                minimumFractionDigits: decimalFormat,
                maximumFractionDigits: decimalFormat,
            })
        }
    }, [decimalFormat])

    return (
        <FormatContext.Provider
            value={{
                dateFormat,
                decimalFormat,
                setDateFormat,
                setDecimalFormat,
                formatDate,
                formatNumber,
            }}
        >
            {children}
        </FormatContext.Provider>
    )
}

export const useFormat = () => {
    const ctx = useContext(FormatContext)
    if (!ctx) {
        throw new Error("useFormat must be used within FormatProvider")
    }
    return ctx
}
